import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  BookOpen,
  Calendar,
  Car,
  ChevronDown,
  ChevronRight,
  CreditCard,
  FileText,
  LayoutDashboard,
  Lock,
  LogOut,
  Menu,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Settings,
  ShieldAlert,
  Sparkles,
  Users,
  Wallet,
  Wrench,
  X,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  getFeatureAccess,
  getPlanLabel,
  getStatusLabel,
  getSubscriptionBannerContent,
} from '../lib/subscription';
import { OnboardingProgressStrip } from '../components/admin/OnboardingProgressStrip';
import { BrandLogo } from '../components/ui/BrandLogo';
import { useAuthStore } from '../store/authStore';
import { fetchJsonWithAuth } from '../lib/authFetch';
import { DEFAULT_SESSION_TIMEOUT_SETTINGS, getSessionTimeoutForRole } from '../lib/sessionTimeouts';

type SessionTimeoutResponse = {
  role: string | null;
  timeoutMinutes: number | null;
  warningMinutes: number;
};

export const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [fabOpen, setFabOpen] = useState(false);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const logoutTimeoutRef = useRef<number | null>(null);
  const warningTimeoutRef = useRef<number | null>(null);

  const roleLabel = (() => {
    if (user?.role === 'admin_taller') return 'ADMIN';
    if (user?.role === 'superadmin') return 'SUPERADMIN';
    if (user?.role === 'tecnico') return 'TECNICO';
    if (user?.role === 'cliente') return 'CLIENTE';
    return user?.role?.replace('_', ' ').toUpperCase() || '';
  })();

  const tallerContext = user?.taller ?? null;
  const planLabel = getPlanLabel(tallerContext);
  const statusLabel = getStatusLabel(tallerContext?.status);
  const subscriptionBanner =
    user?.role === 'admin_taller' || user?.role === 'tecnico'
      ? getSubscriptionBannerContent(tallerContext)
      : null;
  const dismissibleSubscriptionBanner =
    user?.role === 'admin_taller' &&
    (subscriptionBanner?.tone === 'emerald' ||
      subscriptionBanner?.tone === 'sky' ||
      subscriptionBanner?.tone === 'amber');
  const subscriptionBannerDismissKey =
    dismissibleSubscriptionBanner && tallerContext?.id
      ? `subscription_banner_dismissed:${tallerContext.id}:${subscriptionBanner.title}`
      : null;
  const [isSubscriptionBannerDismissed, setIsSubscriptionBannerDismissed] = useState(false);
  const subscriptionBannerAction =
    user?.role === 'tecnico' &&
    (subscriptionBanner?.actionHref?.startsWith('/admin/settings') ||
      subscriptionBanner?.actionHref?.startsWith('/admin/upgrade') ||
      subscriptionBanner?.actionHref?.startsWith('/admin/subscription'))
      ? null
      : subscriptionBanner?.actionLabel && subscriptionBanner?.actionHref
        ? {
            label: subscriptionBanner.actionLabel,
            href: subscriptionBanner.actionHref,
          }
        : null;
  const planLimits = [
    tallerContext?.max_clientes ? `${tallerContext.max_clientes} clientes` : null,
    tallerContext?.max_vehiculos ? `${tallerContext.max_vehiculos} vehículos` : null,
    tallerContext?.max_usuarios ? `${tallerContext.max_usuarios} usuarios` : null,
  ].filter(Boolean) as string[];
  const settingsChildren = [
    { name: 'Suscripción', path: '/admin/subscription' },
    { name: 'Resumen', path: '/admin/settings#resumen' },
    { name: 'Taller', path: '/admin/settings#taller' },
    { name: 'Canales', path: '/admin/settings#canales' },
    { name: 'Automatizaciones', path: '/admin/settings#automatizaciones' },
    { name: 'Cobros', path: '/admin/settings#cobros' },
    { name: 'Agenda', path: '/admin/settings#agenda' },
  ];
  const superadminConfigChildren = [
    { name: 'Resumen', path: '/superadmin/configuracion#resumen' },
    { name: 'Timeout global', path: '/superadmin/configuracion#timeouts' },
  ];
  const canCreateDirectOrder = getFeatureAccess(tallerContext, 'ordenes').allowed;
  const canCreateQuote = getFeatureAccess(tallerContext, 'cotizaciones').allowed;
  const canCreateClient = getFeatureAccess(tallerContext, 'clientes').allowed;
  const hasFabActions = canCreateDirectOrder || canCreateQuote || canCreateClient;
  const [sessionTimeoutConfig, setSessionTimeoutConfig] = useState(() => ({
    timeoutMinutes: getSessionTimeoutForRole(DEFAULT_SESSION_TIMEOUT_SETTINGS, user?.role) || null,
    warningMinutes: DEFAULT_SESSION_TIMEOUT_SETTINGS.warningMinutes,
  }));

  const clearIdleTimers = useCallback(() => {
    if (logoutTimeoutRef.current) {
      window.clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      window.clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  }, []);

  const getIdleTimeout = useCallback(() => {
    return sessionTimeoutConfig.timeoutMinutes ? sessionTimeoutConfig.timeoutMinutes * 60 * 1000 : null;
  }, [sessionTimeoutConfig.timeoutMinutes]);

  const scheduleIdleTimers = useCallback(() => {
    clearIdleTimers();
    setShowIdleWarning(false);

    const idleTimeout = getIdleTimeout();
    if (!idleTimeout) return;

    if (sessionTimeoutConfig.warningMinutes > 0) {
      const warningDelay = Math.max(0, idleTimeout - sessionTimeoutConfig.warningMinutes * 60 * 1000);
      warningTimeoutRef.current = window.setTimeout(() => {
        setShowIdleWarning(true);
      }, warningDelay);
    }

    logoutTimeoutRef.current = window.setTimeout(() => {
      setShowIdleWarning(false);
      logout();
      navigate('/login', { replace: true });
    }, idleTimeout);
  }, [clearIdleTimers, getIdleTimeout, logout, navigate, sessionTimeoutConfig.warningMinutes]);

  const handleActivity = useCallback(() => {
    scheduleIdleTimers();
  }, [scheduleIdleTimers]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getNavItems = () => {
    if (user?.role === 'superadmin') {
      return [
        { name: 'Dashboard', path: '/superadmin', icon: LayoutDashboard },
        { name: 'Talleres', path: '/superadmin/talleres', icon: Wrench },
        { name: 'Planes', path: '/superadmin/planes', icon: CreditCard },
        { name: 'Facturación', path: '/superadmin/facturacion', icon: Wallet },
        { name: 'Configuración', path: '/superadmin/configuracion', icon: Settings, children: superadminConfigChildren },
      ];
    }
    if (user?.role === 'admin_taller' || user?.role === 'tecnico') {
      return [
        { name: 'Dashboard', path: '/admin', icon: LayoutDashboard, feature: 'dashboard' },
        { name: 'Clientes', path: '/admin/clientes', icon: Users, feature: 'clientes' },
        { name: 'Vehículos', path: '/admin/vehiculos', icon: Car, feature: 'vehiculos' },
        { name: 'Cotizaciones', path: '/admin/cotizaciones', icon: FileText, feature: 'cotizaciones' },
        { name: 'Órdenes', path: '/admin/ordenes', icon: Wrench, feature: 'ordenes' },
        { name: 'Citas', path: '/admin/citas', icon: Calendar, feature: 'citas' },
        { name: 'Recordatorios', path: '/admin/recordatorios', icon: Bell, feature: 'recordatorios' },
        { name: 'Inventario', path: '/admin/inventario', icon: Package, feature: 'inventario' },
        ...(user?.role === 'admin_taller'
          ? [
              {
                name: 'Configuración',
                path: '/admin/settings',
                icon: Settings,
                feature: 'settings_general',
                children: settingsChildren,
              },
              {
                name: 'Manual de Uso',
                path: '/admin/manual',
                icon: BookOpen,
              },
            ]
          : []),
      ];
    }
    if (user?.role === 'cliente') {
      return [
        { name: 'Mi Portal', path: '/client', icon: LayoutDashboard },
        { name: 'Mis Vehículos', path: '/client/vehiculos', icon: Car },
        { name: 'Mis Citas', path: '/client/citas', icon: Calendar },
        { name: 'Cotizaciones', path: '/client/cotizaciones', icon: FileText },
        { name: 'Mis Órdenes', path: '/client/ordenes', icon: Wrench },
        { name: 'Pagos', path: '/client/pagos', icon: CreditCard },
      ];
    }
    return [];
  };

  useEffect(() => {
    const fallback = {
      timeoutMinutes: getSessionTimeoutForRole(DEFAULT_SESSION_TIMEOUT_SETTINGS, user?.role) || null,
      warningMinutes: DEFAULT_SESSION_TIMEOUT_SETTINGS.warningMinutes,
    };

    setSessionTimeoutConfig(fallback);

    if (!user?.role) return;
    let cancelled = false;

    const loadTimeoutConfig = async () => {
      try {
        const response = await fetchJsonWithAuth<SessionTimeoutResponse>('/api/app/session-timeouts');
        if (cancelled) return;
        setSessionTimeoutConfig({
          timeoutMinutes: response.timeoutMinutes,
          warningMinutes: Number.isFinite(response.warningMinutes) ? response.warningMinutes : fallback.warningMinutes,
        });
      } catch (error) {
        if (!cancelled) {
          console.warn('No se pudo cargar el timeout global de sesion. Se usan los valores por defecto.', error);
        }
      }
    };

    void loadTimeoutConfig();

    return () => {
      cancelled = true;
    };
  }, [user?.role]);

  const navItems = getNavItems();
  const currentHash = location.hash || '';
  const isPathActive = (path: string) => {
    if (path === '/admin' || path === '/superadmin' || path === '/client') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const isChildActive = (path: string) => {
    const [basePath, hashValue] = path.split('#');
    if (!hashValue) return location.pathname === basePath;
    return location.pathname === basePath && currentHash === `#${hashValue}`;
  };

  const activeNavItem: any = navItems.find((item: any) => {
    if (item.path === location.pathname) return true;
    return Array.isArray(item.children) && item.children.some((child: any) => isChildActive(child.path));
  });
  const activeNavChild = Array.isArray(activeNavItem?.children)
    ? activeNavItem.children.find((child: any) => isChildActive(child.path))
    : null;

  const isGroupActive = (item: any) =>
    Array.isArray(item.children) && item.children.some((child: any) => isChildActive(child.path));

  useEffect(() => {
    if (!subscriptionBannerDismissKey || typeof window === 'undefined') {
      setIsSubscriptionBannerDismissed(false);
      return;
    }

    setIsSubscriptionBannerDismissed(window.localStorage.getItem(subscriptionBannerDismissKey) === 'true');
  }, [subscriptionBannerDismissKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem('dashboard_sidebar_collapsed');
    setIsDesktopSidebarCollapsed(stored === 'true');
  }, []);

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      let changed = false;

      navItems.forEach((item: any) => {
        if (Array.isArray(item.children) && item.children.length > 0 && isGroupActive(item) && !prev[item.path]) {
          next[item.path] = true;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [location.pathname, currentHash, navItems]);

  const toggleDesktopSidebar = () => {
    setIsDesktopSidebarCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('dashboard_sidebar_collapsed', String(next));
      }
      return next;
    });
  };

  const toggleGroup = (path: string) => {
    setExpandedGroups((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const handleDismissSubscriptionBanner = () => {
    if (!subscriptionBannerDismissKey || typeof window === 'undefined') return;
    window.localStorage.setItem(subscriptionBannerDismissKey, 'true');
    setIsSubscriptionBannerDismissed(true);
  };

  useEffect(() => {
    scheduleIdleTimers();

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, { passive: true });
    });

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity);
      });
      clearIdleTimers();
    };
  }, [handleActivity, scheduleIdleTimers, clearIdleTimers]);

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={clsx(
        "fixed inset-y-0 left-0 z-50 bg-gradient-to-b from-slate-900 to-slate-800 text-white flex flex-col transition-all duration-300 ease-in-out lg:static lg:translate-x-0",
        isDesktopSidebarCollapsed ? "lg:w-20" : "lg:w-64",
        !isDesktopSidebarCollapsed && "w-64",
        isSidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className={clsx("flex items-center justify-between", isDesktopSidebarCollapsed ? "px-3 py-5 lg:px-3" : "p-6")}>
          <div className={clsx(isDesktopSidebarCollapsed ? "flex w-full justify-center" : "")}>
            {isDesktopSidebarCollapsed ? (
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white p-1 shadow-sm ring-1 ring-white/15">
                <BrandLogo variant="mark" className="h-full w-full" />
              </div>
            ) : (
              <div className="min-w-0">
                <BrandLogo variant="full" className="h-14 w-[220px] max-w-full" imgClassName="object-left" />
                <p className="text-xs text-slate-400 mt-1">{roleLabel}</p>
              </div>
            )}
          </div>
          <button
            type="button"
            aria-label="Cerrar menu"
            className="rounded-xl p-2 text-slate-400 transition hover:bg-white/5 hover:text-white lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {!isDesktopSidebarCollapsed && (
          <div className="hidden lg:flex px-6 -mt-2 pb-2 justify-end">
            <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">Menú</span>
          </div>
        )}
        
        <nav className={clsx("flex-1 space-y-1 mt-4 overflow-y-auto", isDesktopSidebarCollapsed ? "px-2" : "px-4")}>
          {navItems.map((item: any) => {
            const Icon = item.icon;
            const isActive = isPathActive(item.path);
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            const isExpanded = !!expandedGroups[item.path];
            const access = item.feature ? getFeatureAccess(tallerContext, item.feature) : { allowed: true, reason: null };
            const isLocked = !access.allowed;
            const lockLabel = access.reason === 'billing' ? statusLabel : 'Plan superior';
            const lockedItemClass = isActive
              ? 'bg-amber-500/15 text-amber-100'
              : 'text-amber-200 hover:bg-amber-500/10 hover:text-white';
            return (
              <div key={item.name} className="space-y-1">
                {hasChildren && !isDesktopSidebarCollapsed && !isLocked ? (
                  <div
                    className={clsx(
                      'flex items-center rounded-lg transition-colors',
                      isActive || isGroupActive(item)
                        ? 'bg-emerald-500/10 text-emerald-400'
                        : 'text-slate-300 hover:bg-white/5 hover:text-white'
                    )}
                  >
                    <Link
                      to={item.path}
                      onClick={() => setIsSidebarOpen(false)}
                      className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-sm font-medium"
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                      <span className="min-w-0 flex-1 text-left">{item.name}</span>
                    </Link>
                    <button
                      type="button"
                      onClick={() => toggleGroup(item.path)}
                      className="rounded-r-lg px-3 py-2.5 text-current/80 transition hover:text-current"
                      aria-label={isExpanded ? `Contraer ${item.name}` : `Expandir ${item.name}`}
                    >
                      {isExpanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                    </button>
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => setIsSidebarOpen(false)}
                    title={isDesktopSidebarCollapsed ? item.name : isLocked ? `${item.name} bloqueado: ${lockLabel}` : undefined}
                    className={clsx(
                      'flex items-center rounded-lg text-sm font-medium transition-colors',
                      isDesktopSidebarCollapsed ? 'justify-center px-0 py-3' : 'gap-3 px-3 py-2.5',
                      isLocked
                        ? lockedItemClass
                        : isActive
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'text-slate-300 hover:bg-white/5 hover:text-white'
                    )}
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    {!isDesktopSidebarCollapsed && (
                      <>
                        <span className="flex-1">{item.name}</span>
                        {isLocked && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-amber-300/20 bg-amber-400/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-amber-100">
                            <Lock className="h-3 w-3" />
                            {access.reason === 'billing' ? statusLabel : 'Pro'}
                          </span>
                        )}
                      </>
                    )}
                  </Link>
                )}
                {!isDesktopSidebarCollapsed && hasChildren && isExpanded && !isLocked && (
                  <div className="ml-8 space-y-1">
                    {item.children.map((child: any) => {
                      const childActive = isChildActive(child.path);
                      return (
                        <Link
                          key={child.name}
                          to={child.path}
                          onClick={() => setIsSidebarOpen(false)}
                          className={clsx(
                            'block px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                            childActive
                              ? 'text-emerald-300 bg-emerald-500/10'
                              : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                          )}
                        >
                          {child.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className={clsx("border-t border-white/10 shrink-0", isDesktopSidebarCollapsed ? "p-3" : "p-4")}>
          <div className={clsx("flex items-center mb-4", isDesktopSidebarCollapsed ? "justify-center" : "gap-3 px-2")}>
            <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-sm font-bold shrink-0">
              {user?.name.charAt(0)}
            </div>
            {!isDesktopSidebarCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            title={isDesktopSidebarCollapsed ? 'Cerrar sesión' : undefined}
            className={clsx(
              'w-full rounded-lg text-sm font-medium text-slate-300 hover:bg-white/5 hover:text-white transition-colors',
              isDesktopSidebarCollapsed ? 'flex items-center justify-center px-0 py-2.5' : 'flex items-center gap-3 px-3 py-2'
            )}
          >
            <LogOut className="w-5 h-5" />
            {!isDesktopSidebarCollapsed && 'Cerrar Sesión'}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex min-h-dvh flex-1 flex-col min-w-0 overflow-x-hidden md:h-screen md:overflow-hidden">
        {showIdleWarning && (user?.role === 'admin_taller' || user?.role === 'tecnico') && (
          <div className="flex flex-col gap-3 border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between lg:px-8">
            <span>Tu sesión se cerrará en 20 minutos por inactividad.</span>
            <button
              onClick={handleActivity}
              className="px-3 py-1.5 rounded-md bg-amber-200/70 hover:bg-amber-200 text-amber-900 font-medium"
            >
              Seguir activo
            </button>
          </div>
        )}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8 shrink-0">
          <div className="flex items-center gap-4">
            <button
              className="lg:hidden p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
              onClick={() => setIsSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            <button
              className="hidden lg:inline-flex p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
              onClick={toggleDesktopSidebar}
              title={isDesktopSidebarCollapsed ? 'Expandir menú' : 'Compactar menú'}
            >
              {isDesktopSidebarCollapsed ? <PanelLeftOpen className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            </button>
            <h2 className="text-lg font-semibold text-slate-800 truncate">
              {activeNavChild?.name || activeNavItem?.name || 'Dashboard'}
            </h2>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            {(user?.role === 'admin_taller' || user?.role === 'tecnico') && (
              <div className="hidden items-center gap-2 md:flex">
                <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                  {planLabel}
                </span>
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                  {statusLabel}
                </span>
              </div>
            )}
            <button className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100">
              <Bell className="w-5 h-5" />
            </button>
          </div>
        </header>
        {user?.role === 'admin_taller' && <OnboardingProgressStrip context={tallerContext} />}
        {subscriptionBanner && !isSubscriptionBannerDismissed && (
          <div
            className={clsx(
              'border-b px-4 py-3 text-sm lg:px-8',
              subscriptionBanner.tone === 'rose'
                ? 'border-rose-200 bg-rose-50 text-rose-950'
                : subscriptionBanner.tone === 'amber'
                  ? 'border-amber-200 bg-amber-50 text-amber-950'
                  : subscriptionBanner.tone === 'sky'
                    ? 'border-sky-200 bg-sky-50 text-sky-950'
                    : 'border-emerald-200 bg-emerald-50 text-emerald-950'
            )}
          >
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex items-start gap-3">
                <div
                  className={clsx(
                    'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl',
                    subscriptionBanner.tone === 'rose'
                      ? 'bg-rose-100 text-rose-700'
                      : subscriptionBanner.tone === 'amber'
                        ? 'bg-amber-100 text-amber-700'
                        : subscriptionBanner.tone === 'sky'
                          ? 'bg-sky-100 text-sky-700'
                          : 'bg-emerald-100 text-emerald-700'
                  )}
                >
                  {subscriptionBanner.tone === 'emerald' || subscriptionBanner.tone === 'sky' ? (
                    <Sparkles className="h-5 w-5" />
                  ) : (
                    <ShieldAlert className="h-5 w-5" />
                  )}
                </div>
                <div>
                  <p className="font-semibold">{subscriptionBanner.title}</p>
                  <p className="mt-1 max-w-3xl text-sm opacity-80">{subscriptionBanner.description}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
                {subscriptionBanner.badge && (
                  <span className="rounded-full border border-current/10 bg-white/80 px-3 py-1">
                    {subscriptionBanner.badge}
                  </span>
                )}
                <span className="rounded-full border border-current/10 bg-white/60 px-3 py-1">Plan: {planLabel}</span>
                <span className="rounded-full border border-current/10 bg-white/60 px-3 py-1">Estado: {statusLabel}</span>
                {planLimits.map((limit) => (
                  <span key={limit} className="rounded-full border border-current/10 bg-white/60 px-3 py-1">
                    {limit}
                  </span>
                ))}
                {subscriptionBannerAction && (
                  <Link
                    to={subscriptionBannerAction.href}
                    className="inline-flex items-center justify-center rounded-full bg-slate-950 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-800"
                  >
                    {subscriptionBannerAction.label}
                  </Link>
                )}
                {dismissibleSubscriptionBanner && (
                  <button
                    type="button"
                    onClick={handleDismissSubscriptionBanner}
                    className="inline-flex items-center justify-center rounded-full border border-current/10 bg-white/70 p-1.5 text-current transition hover:bg-white"
                    aria-label="Cerrar banner del plan"
                    title="Cerrar banner"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        <div className="relative flex-1 overflow-auto p-4 lg:p-8">
          {children}
          
          {/* Floating Action Button for Admin/Tecnico */}
          {(user?.role === 'admin_taller' || user?.role === 'tecnico') && hasFabActions && (
            <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
              {fabOpen && (
                <div className="flex flex-col gap-2 items-end mb-2">
                  {canCreateClient && (
                    <button
                      onClick={() => { navigate('/admin/clientes?new=true'); setFabOpen(false); }}
                      className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-lg border border-slate-100 hover:bg-slate-50 text-sm font-medium text-slate-700 transition-all hover:scale-105"
                    >
                      Nuevo Cliente <Users className="w-4 h-4 text-blue-600" />
                    </button>
                  )}
                  {canCreateQuote && (
                    <button
                      onClick={() => { navigate('/admin/cotizaciones?new=true'); setFabOpen(false); }}
                      className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-lg border border-slate-100 hover:bg-slate-50 text-sm font-medium text-slate-700 transition-all hover:scale-105"
                    >
                      Nueva Cotización <FileText className="w-4 h-4 text-emerald-600" />
                    </button>
                  )}
                  {canCreateDirectOrder && (
                    <button
                      onClick={() => { navigate('/admin/ordenes?directa=true'); setFabOpen(false); }}
                      className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-lg border border-slate-100 hover:bg-slate-50 text-sm font-medium text-slate-700 transition-all hover:scale-105"
                    >
                      Orden Directa <Wrench className="w-4 h-4 text-emerald-600" />
                    </button>
                  )}
                </div>
              )}
              <button
                onClick={() => setFabOpen(!fabOpen)}
                className="bg-emerald-600 text-white w-14 h-14 rounded-full shadow-xl flex items-center justify-center hover:bg-emerald-700 transition-transform hover:scale-105 active:scale-95"
              >
                <Plus className={`w-6 h-6 transition-transform duration-200 ${fabOpen ? 'rotate-45' : ''}`} />
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
